import React, { useState, useEffect } from 'react';
import { Download, Share, X, Smartphone } from 'lucide-react';

/**
 * Detects platform and install state, and shows the right install UI:
 * - Android/Chrome: captures the native `beforeinstallprompt` event and
 *   shows our own styled button that triggers it. Chrome only fires this
 *   event if the manifest + service worker are correctly set up.
 * - iOS Safari: there is no install prompt API at all. We detect iOS and
 *   show explicit instructions (Share -> Add to Home Screen) instead,
 *   since most people don't know this feature exists otherwise.
 * - Already installed (running in standalone mode): shows nothing.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroidBanner, setShowAndroidBanner] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Already installed and running as a standalone app? Don't show anything.
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true; // iOS-specific flag
    if (isStandalone) return;

    // Respect a previous dismissal for this session
    if (sessionStorage.getItem('aizaan_install_dismissed') === '1') {
      return;
    }

    const isIOS = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());

    if (isIOS) {
      // No native prompt exists on iOS — just show our instructions banner
      setShowIOSInstructions(true);
      return;
    }

    // Android/Chrome: listen for the browser's install-eligibility event
    const handler = (e) => {
      e.preventDefault(); // stop Chrome's default mini-banner
      setDeferredPrompt(e);
      setShowAndroidBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log('Install prompt outcome:', outcome);
    setDeferredPrompt(null);
    setShowAndroidBanner(false);
  };

  const handleDismiss = () => {
    setShowAndroidBanner(false);
    setShowIOSInstructions(false);
    setDismissed(true);
    sessionStorage.setItem('aizaan_install_dismissed', '1');
  };

  if (dismissed || (!showAndroidBanner && !showIOSInstructions)) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 16,
      left: 16,
      right: 16,
      maxWidth: 480,
      margin: '0 auto',
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      padding: '16px 18px',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
    }}>
      <div style={{
        width: 38, height: 38,
        background: 'var(--green)',
        borderRadius: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Smartphone size={18} color="var(--navy, #0A0F1E)" />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {showAndroidBanner ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
              Install Aizaan
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5 }}>
              Add Aizaan to your home screen for one-tap access and faster calling.
            </div>
            <button
              onClick={handleAndroidInstall}
              className="btn btn-primary"
              style={{ fontSize: 13, padding: '7px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Download size={14} /> Install
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
              Add Aizaan to your Home Screen
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Tap <Share size={12} style={{ display: 'inline', verticalAlign: 'middle', margin: '0 2px' }} /> Share, then scroll down and tap <strong style={{ color: 'var(--text-primary)' }}>"Add to Home Screen"</strong>.
            </div>
          </>
        )}
      </div>

      <button
        onClick={handleDismiss}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', padding: 4, flexShrink: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
